import { Helmet } from 'react-helmet-async';

// SEO configuration for different pages
const seoConfig = {
  home: {
    title: 'KOSS LAB | Оборудование для восстановления и реабилитации',
    description: 'Профессиональное оборудование для восстановления, реабилитации и поддержки тела. Профилакторы Евминова, петли Глиссона, нейротренажёры. Экспертный подбор от врача-невролога Виктора Косса.',
    keywords: 'профилактор Евминова, петля Глиссона, нейротренажёр, реабилитация, восстановление позвоночника, лечение спины, KOSS LAB',
    canonical: 'https://kosslab.ru/',
  },
  catalog: {
    title: 'Каталог оборудования | KOSS LAB',
    description: 'Полный каталог профессионального оборудования для восстановления и реабилитации. Профилакторы, тренажёры, массажное оборудование с доставкой по России.',
    keywords: 'каталог медицинского оборудования, купить профилактор, тренажёры для реабилитации, массажное оборудование',
    canonical: 'https://kosslab.ru/catalog',
  },
  about: {
    title: 'О проекте и эксперте | KOSS LAB',
    description: 'KOSS LAB — экспертная платформа доктора Виктора Косса, врача-невролога и кандидата медицинских наук. Только проверенное оборудование из собственной практики.',
    keywords: 'Виктор Косс, врач невролог, спортивная неврология, реабилитация, эксперт',
    canonical: 'https://kosslab.ru/about',
  },
  b2b: {
    title: 'Для клиник и специалистов | KOSS LAB',
    description: 'Профессиональное оборудование для клиник, реабилитационных центров и спортивных учреждений. Оптовые поставки, обучение персонала, техническая поддержка.',
    keywords: 'оборудование для клиник, оснащение реабилитационного центра, медицинское оборудование оптом, B2B',
    canonical: 'https://kosslab.ru/b2b',
  },
  contact: {
    title: 'Контакты и консультация | KOSS LAB',
    description: 'Получите бесплатную консультацию по подбору оборудования. Свяжитесь с экспертами KOSS LAB для индивидуального подбора решения.',
    keywords: 'консультация по оборудованию, связаться с KOSS LAB, заказать звонок',
    canonical: 'https://kosslab.ru/contact',
  },
  privacy: {
    title: 'Политика конфиденциальности | KOSS LAB',
    description: 'Политика конфиденциальности и обработки персональных данных на сайте KOSS LAB.',
    keywords: 'политика конфиденциальности, персональные данные',
    canonical: 'https://kosslab.ru/privacy',
  },
  terms: {
    title: 'Пользовательское соглашение | KOSS LAB',
    description: 'Условия использования сайта KOSS LAB и правила оказания услуг.',
    keywords: 'пользовательское соглашение, условия использования',
    canonical: 'https://kosslab.ru/terms',
  },
  offer: {
    title: 'Публичная оферта | KOSS LAB',
    description: 'Публичная оферта на продажу товаров интернет-магазина KOSS LAB.',
    keywords: 'публичная оферта, договор купли-продажи',
    canonical: 'https://kosslab.ru/offer',
  },
};

// SEO Component
const SEO = ({ page, product, category }) => {
  let config = seoConfig[page] || seoConfig.home;
  
  // Dynamic product page SEO
  if (product) {
    config = {
      title: `${product.name} — купить | KOSS LAB`,
      description: `${product.name} — ${product.tagline || product.description?.slice(0, 150)}. Цена: ${product.price?.toLocaleString()} ₽. Доставка по России.`,
      keywords: `${product.name}, купить ${product.name}, ${product.category_name}, KOSS LAB`,
      canonical: `https://kosslab.ru/product/${product.slug || product.id}`,
      image: product.image_url,
      price: product.price,
    };
  }
  
  // Dynamic category page SEO
  if (category) {
    config = {
      ...seoConfig.catalog,
      title: `${category.name} | Каталог KOSS LAB`,
      description: `${category.description || `Оборудование категории ${category.name}`}. Профессиональные решения с доставкой по России.`,
      canonical: `https://kosslab.ru/catalog?category=${category.id}`,
    };
  }

  const ogImage = config.image || 'https://kosslab.ru/images/og-image.png';

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{config.title}</title>
      <meta name="title" content={config.title} />
      <meta name="description" content={config.description} />
      <meta name="keywords" content={config.keywords} />
      <link rel="canonical" href={config.canonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={product ? 'product' : 'website'} />
      <meta property="og:url" content={config.canonical} />
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />
      <meta property="og:image" content={ogImage} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={config.canonical} />
      <meta property="twitter:title" content={config.title} />
      <meta property="twitter:description" content={config.description} />
      <meta property="twitter:image" content={ogImage} />
      
      {/* Product specific meta */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content="RUB" />
          <meta property="og:price:amount" content={product.price} />
          <meta property="og:price:currency" content="RUB" />
        </>
      )}
    </Helmet>
  );
};

// Product JSON-LD Schema
const ProductSchema = ({ product }) => {
  if (!product) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.image_url,
    "brand": {
      "@type": "Brand",
      "name": "KOSS LAB"
    },
    "offers": {
      "@type": "Offer",
      "url": `https://kosslab.ru/product/${product.slug || product.id}`,
      "priceCurrency": "RUB",
      "price": product.price,
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "KOSS LAB"
      }
    }
  };
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema for homepage
const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema
const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export { SEO, ProductSchema, FAQSchema, BreadcrumbSchema, seoConfig };
export default SEO;
